import { Switch } from 'antd';

interface SlotToggleRowProps {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
  // Shown INSTEAD of `description` while disabled (e.g. a muted explanatory
  // hint). Falls back to `description` if omitted.
  disabledHint?: string;
  // Rendered below the switch row, ONLY while !disabled && checked — e.g.
  // the auto-select row's "would pick…" preview / no-eligible-slot warning.
  // The recurring row passes nothing here (no preview, per spec §4.4).
  extra?: React.ReactNode;
  // When false, the row draws NO border/radius of its own — it is a plain
  // padded row meant to sit inside a shared bordered container (SlotModal
  // stacks the auto-select + recurring rows in one card, divided by a
  // hairline, instead of two separate bordered boxes). Defaults to true so
  // standalone callers (ChangeSlotModal edit mode) keep their own border.
  bordered?: boolean;
  // Compact one-line layout for SlotModal's secondary automation strip: label +
  // 11px helper share the left column, switch on the right. Standalone callers
  // omit it and get the roomier full-width-description layout.
  compact?: boolean;
}

// v2-task-10fe — extracted from the original T9 AutoSelectToggleRow so the
// SAME bordered radius-md row construction (rounded-xl border p-3, label +
// muted description + restyled `.pickup-slot-switch` AntD Switch) can be
// reused verbatim by both the auto-select toggle (T9) and the new
// recurring-pickup toggle (§4.4), with ZERO new visual vocabulary. Each
// caller still renders its OWN bordered row — SlotModal stacks two of these
// with a gap, never merges them into one taller row.
const SlotToggleRow = ({
  label,
  description,
  checked,
  onChange,
  disabled = false,
  disabledHint,
  extra,
  bordered = true,
  compact = false,
}: SlotToggleRowProps) => {
  const desc = disabled && disabledHint ? disabledHint : description;

  // v2-task-12fix (H1): Tailwind runs with `corePlugins.preflight` OFF, so a
  // bare `border` carries only border-WIDTH (style stays `none`, collapses to
  // 0px). `border-solid` compiles (important:true) to `border-style:solid
  // !important`, restoring the 1px row border.
  // `bordered=false` (inside SlotModal's shared card) drops the row's own
  // border+radius so it reads as one section of the card divided by a hairline.
  const wrapperClass = bordered ? 'rounded-xl border border-solid' : '';
  const wrapperStyle = bordered ? { borderColor: 'rgb(232,230,227)' } : undefined;

  // Compact (SlotModal's secondary "automation" strip): label + a small 11px
  // helper line share the LEFT column, the switch centres against the whole
  // block on the right. One tight row — the automation options are secondary to
  // the slot list and must not out-weigh it. `min-w-0` lets long Arabic copy
  // wrap instead of shoving the switch off the row.
  if (compact) {
    return (
      <div className={wrapperClass} style={wrapperStyle}>
        <div className='flex items-center justify-between gap-3 px-3.5 py-2.5'>
          <div className='min-w-0 text-start'>
            {/* Preflight is OFF and this modal is outside `.sd-scope`, so a bare
                <p> keeps the UA's ~14px top+bottom margins — which bloated each
                row ~28px. Zero them explicitly (m-0), then re-add only the 2px
                gap between label and helper. */}
            <p className='m-0 text-sm font-semibold leading-tight text-[rgb(57,57,60)]'>{label}</p>
            <p className='mb-0 mt-0.5 text-[11px] leading-snug text-[rgb(115,123,140)]'>{desc}</p>
          </div>
          <Switch
            className='pickup-slot-switch shrink-0'
            checked={checked}
            disabled={disabled}
            onChange={(value) => onChange(value)}
            aria-label={label}
          />
        </div>
        {/* The auto-select preview / no-slot warning chip renders full-width
            below the toggle line, inside the same card. Recurring passes no
            `extra`, so nothing appends for it. */}
        {!disabled && checked && extra ? <div className='px-3.5 pb-2.5'>{extra}</div> : null}
      </div>
    );
  }

  // Standalone (non-compact) — e.g. ChangeSlotModal edit mode: label+switch on
  // one line, full-width description below.
  return (
    <div className={`${wrapperClass} px-3.5 py-3`} style={wrapperStyle}>
      <div className='flex items-center justify-between gap-3'>
        <p className='text-sm font-semibold leading-tight'>{label}</p>
        <Switch
          className='pickup-slot-switch shrink-0'
          checked={checked}
          disabled={disabled}
          onChange={(value) => onChange(value)}
          aria-label={label}
        />
      </div>
      <p className='mt-1 text-xs leading-snug text-[rgb(115,123,140)]'>{desc}</p>

      {!disabled && checked ? extra : null}
    </div>
  );
};

export default SlotToggleRow;
